import React from 'react'
import AgTechGrid from '@agtech/web/components/Grids/AgTechGrid'
import { AgTechGridTextControl } from '@agtech/web/components/Grids/Controls/AgTechGridTextControl'
import { AgTechGridEditConfiguration } from '@agtech/web/components/Grids/Features/AgTechGridEditing'
import { SaleLot, isLotTheSame } from '@sales/data/lots/LotData'
import { useLotDataWriteLotAction } from '@sales/data/lots/LotDataActions'
import LotStatusBadge from '../badges/LotStatusBadge'
import { useLotDataGetSaleLotDataQuery, useLotDataGetSaleLotsQuery } from '@sales/data/lots/LotDataQueries'
import { useAgTechQueryResultProjection } from '@agtech/core/data/queries/AgTechDataQueries'
import LotCategoryGridLookup from '../../../lotCategories/components/lookups/LotCategoryGridLookup'

export type SaleLotModificationGridProps = {
    lotFilter?: (lot: SaleLot) => boolean
}

const SaleLotModificationGrid = (props: SaleLotModificationGridProps) => {
    const gridQuery = useSaleLotModificationGridQuery(props);
    const writeLotAction = useLotDataWriteLotAction();

    return (
        <AgTechGrid
            data={{
                itemQuery: gridQuery,
            }}
            header={{
                title: 'Edit Sale Lots',
                // content: () => <LotSaleOrderUpdateDialog.Button />
            }}
            columns={[
                {
                    header: 'Sale Order',
                    value: l => l.saleOrder,
                    width: '100px',
                    classes: 'bg-slate-100 centered emphasized'
                },
                {
                    header: 'Lot Number',
                    width: '100px',
                    value: (l, _, update) => (
                        <AgTechGridTextControl
                            value={l.lotNumber ?? ''}
                            onUpdated={val => {
                                update(e => ({
                                    ...e,
                                    lotNumber: val
                                }));
                            }}
                        />
                    )
                },
                {
                    header: 'Lot Description',
                    value: (l, _, update) => (
                        <AgTechGridTextControl
                            value={l.lotDescription ?? ''}
                            onUpdated={val => {
                                update(e => ({
                                    ...e,
                                    lotDescription: val
                                }));
                            }}
                        />
                    ),
                    size: 2
                },
                {
                    header: 'Lot Category',
                    width: '200px',
                    value: (l, context, update) => (
                        <LotCategoryGridLookup
                            lot={l}
                            onLotCategorySelected={async sel => {
                                update(e => ({
                                    ...e,
                                    lotCategoryId: sel?.id
                                }));
                            }}
                        />
                    )
                },
                {
                    header: 'Status',
                    value: l => <LotStatusBadge lot={l} />,
                    width: '260px',
                    classes: 'px-3'
                }
            ]}
            isRowEntityTheSame={(prev, curr) => {
                return isLotTheSame(prev, curr);
            }}
            editConfig={{
                onEntityUpdated: async props => {
                    await props.actionExecutor.executeAction(writeLotAction, {
                        originalEntity: props.originalItem,
                        submittedEntity: props.entity.item
                    });
        
                    return {
                        success: {
                            data: props.entity.item ?? props.originalItem
                        }
                    }
                }
            }}
            searchConfig={{
                options: [
                    { field: 'Lot Number', value: lot => lot.lotNumber },
                    { field: 'Description', value: lot => lot.lotDescription },
                    { field: 'Purchasing Bidder', value: lot => lot.bidderNumber ?? '' },
                ]
            }}
        />
    );
};

const useSaleLotModificationGridQuery = (props: SaleLotModificationGridProps) => {
    const lotDataQuery = useLotDataGetSaleLotDataQuery();

    return useAgTechQueryResultProjection({
        queryRun: lotDataQuery,
        projection: lotData => {
            const lotFilter = props.lotFilter ?? (() => true);
            const lots = Object.values(lotData.lots).filter(lotFilter);

            return {
                items: lots,
                context: lotData
            }
        }
    });
}

export default React.memo(SaleLotModificationGrid, () => true) as typeof SaleLotModificationGrid;